import {
  Component,
  ChangeDetectionStrategy,
  ComponentRef,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, switchMap, of, finalize, shareReplay, map } from 'rxjs';
import { untilDestroyed } from '@ngneat/until-destroy';

import { DprConsumablesGridComponent } from '@dpr-details/components';
import { DprDetailsPageBaseComponent } from '@dpr-details/models/component';
import { DprConsumableDetailGridData } from '@dpr-details/models/grid';
import { DprConsumableGridService } from '@dpr-details/services/grid';
import { getDefaultSubMenuItems } from '@dpr-details/utils';
import { GridSync } from '@grid/models';
import { DprConsumable, DprDetailsSync } from '@shared/models/dpr';
import { ScreenSizeService, ToastNotificationService } from '@shared/services';
import { StaticMessage } from '@shared/enums';
import { getStaticMessage } from '@shared/utils';
import { DprDetailsService } from '@dpr-details/services';
import { DprDetailsSyncModelBuilderService } from '@dpr-details/services/sync';
import { DprConsumableGridSync } from '@dpr-details/models/sync';
import { DprAttachmentsGridData } from '@shared/models/grid';
import { DprAttachmentService } from '@shared/services/dpr';
import { NavigationMenuItem } from '@shared/models';

@Component({
  selector: 'app-dpr-consumables-page',
  templateUrl: './dpr-consumables-page.component.html',
  styleUrls: ['./dpr-consumables-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprConsumablesPageComponent
  extends DprDetailsPageBaseComponent<DprConsumableDetailGridData>
  implements OnInit
{
  labeling = {
    gridEntityName: $localize`:@@dprDetails.dprConsumablesPage.txtConsumablesRegistrations:Consumables Registrations`,
  };

  StaticMessage = StaticMessage;

  preselectedItem: string | number;
  navigationSelectItems: NavigationMenuItem[] = getDefaultSubMenuItems();

  constructor(
    private dprConsumablesGridService: DprConsumableGridService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    private dprDetailsService: DprDetailsService,
    protected activatedRoute: ActivatedRoute,
    protected dprAttachmentService: DprAttachmentService,
    private toastNotificationService: ToastNotificationService,
    protected screenSizeService: ScreenSizeService
  ) {
    super(activatedRoute, dprAttachmentService);
  }

  private get dprConsumable(): DprConsumable {
    return this.dprDetails.dprConsumable;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.preselectedItem =
      this.activatedRoute?.firstChild?.snapshot?.url[0]?.path;
  }

  save(
    _?,
    isAutoSync = false,
    isAutoSyncFromAdminChange = false
  ): Observable<boolean> {
    if (!this.isDirty$.value || this.isSaving) {
      throw new Error(
        'Unable to save data. There are either no changes to be saved or there is already a save in progress'
      );
    }

    this.isSaving = true;

    if (
      this.dprConsumablesGridService.shouldTriggerAutoFill(
        this.registrationsGridData,
        this.selectedSubproject?.data.subprojectConfiguration
          .enableStockForConsumables
      )
    ) {
      this.autoFillEmptyItems();
    }

    // store activated component changes
    this.storeComponentChanges(this.activatedComponent);

    const validateChanges$ =
      isAutoSync || isAutoSyncFromAdminChange
        ? of(true)
        : this.validateChanges(this.attachmentsChanges);

    const saveChanges$ = validateChanges$.pipe(
      untilDestroyed(this),
      switchMap((valid) => {
        if (!valid) {
          return of(false);
        }

        return this.saveChanges(
          this.registrationsChanges,
          this.attachmentsChanges,
          this.comment,
          isAutoSyncFromAdminChange
        );
      }),
      finalize(() => {
        this.isSaving = false;
      }),
      shareReplay()
    );

    saveChanges$.pipe(untilDestroyed(this)).subscribe();

    return saveChanges$.pipe(map((result) => !!result));
  }

  onSelectionChange(currentSelection: string | number): void {
    this.preselectedItem = currentSelection;
  }

  protected setupData(): void {
    this.registrationsGridData = this.dprConsumablesGridService.buildGridData(
      this.selectedSubproject.data.consumableList,
      this.dprConsumable?.dprConsumableDetails
    );
    this.attachmentsGridData = this.dprAttachmentService.mapToGridData(
      this.dprConsumable?.dprConsumableAttachments
    );
    this.comment = this.dprConsumable.comment;
  }

  protected isRegistrationComponent(component: ComponentRef<any>): boolean {
    return component instanceof DprConsumablesGridComponent;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected getData(): void {}

  protected getOfflineRegistrationsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): GridSync<DprConsumableDetailGridData> {
    const offlineChanges =
      dprDetailsOfflineChanges?.dprConsumable?.syncDprConsumableDetails;
    if (!offlineChanges) {
      return null;
    }

    const offlineGridData = this.dprConsumablesGridService.buildGridData(
      this.selectedSubproject.data.consumableList,
      [
        ...(offlineChanges.addedList ?? []),
        ...(offlineChanges.updatedList ?? []),
      ]
    );

    return this.getOfflineGridDataChanges(offlineGridData, offlineChanges);
  }

  protected getOfflineAttachmentsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): GridSync<DprAttachmentsGridData> {
    const offlineChanges =
      dprDetailsOfflineChanges?.dprConsumable?.syncDprConsumableAttachments;
    return this.getOfflineAttachmentGridDataChanges(offlineChanges);
  }

  //#region Private methods

  private validateChanges(
    attachmentsChanges: GridSync<DprAttachmentsGridData>
  ): Observable<boolean> {
    if (!this.dprAttachmentService.validateChanges(attachmentsChanges)) {
      return of(false);
    }

    return of(true);
  }

  private saveChanges(
    registrationsChanges: GridSync<DprConsumableDetailGridData>,
    attachmentsChanges: GridSync<DprAttachmentsGridData>,
    comment: string,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprConsumable> {
    const syncDprConsumableDetails =
      this.dprConsumablesGridService.mapToGridSync(
        this.dprConsumable.id,
        registrationsChanges
      );

    const syncData: DprConsumableGridSync =
      this.dprDetailsSyncModelBuilderService.forDprConsumable(
        this.selectedSubproject.id,
        this.dprDetails.id,
        this.dprConsumable.id,
        syncDprConsumableDetails,
        attachmentsChanges,
        comment
      );

    const saveChanges$ = this.dprDetailsService
      .syncDprConsumable(syncData, isAutoSyncFromAdminChange)
      .pipe(shareReplay());

    saveChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.onSaveSuccess(result);
      },
      error: (error: HttpErrorResponse) => {
        this.saveErrorHandlerService.onSaveError(
          this.customTabNames.consumablesTabName,
          this.selectedSubproject.id,
          error
        );
      },
    });

    return saveChanges$;
  }

  private onSaveSuccess(result: DprConsumable): void {
    this.refreshData();
    this.offlineFacade.setDprConsumable(result, this.dprDetails.id);
  }

  private autoFillEmptyItems(): void {
    if (!this.registrationsGridData?.length) {
      return;
    }

    this.toastNotificationService.info(
      getStaticMessage(
        StaticMessage.AutoFillText,
        this.customTabNames.consumablesTabName
      ),
      false,
      getStaticMessage(
        StaticMessage.AutoFillTitle,
        this.customTabNames.consumablesTabName
      )
    );

    if (
      this.selectedSubproject?.data.subprojectConfiguration
        .enableStockForConsumables
    ) {
      this.registrationsGridData.forEach((x) => {
        if (x.added == null) {
          this.registrationsGridApi.editCell(x, 'added', 0);
        }
        if (x.used == null) {
          this.registrationsGridApi.editCell(x, 'used', 0);
        }
      });
    } else {
      this.registrationsGridData.forEach((x) => {
        if (x.today == null) {
          this.registrationsGridApi.editCell(x, 'today', 0);
        }
      });
    }

    this.registrationsChanges = this.registrationsGridApi.getChanges();
  }

  //#endregion
}
